import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex
} from '@chakra-ui/react'
import type { DatesSetArg, EventInput } from '@fullcalendar/core'
import type FullCalendar from '@fullcalendar/react'
import moment, { type Moment } from 'moment'
import queryString from 'query-string'
import React, { useCallback, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useClinicianSchedulingControllerGetClinicianSchedule } from '~/clinician-api'
import type {
  AppointmentDto,
  ScheduleResponseDto
} from '~/clinician-api/models'
import { CalendarSidebar } from './components/calendar-sidebar'
import { useCalendarModals } from './components/event-modals/event-modals-manager'
import {
  CalendarView,
  FullSizeCalendar,
  useIsMobile,
  useIsTablet
} from './components/full-size-calendar'
import { CalendarProvider, useCalendar } from './context/calendar.context'
import { CalendarEventProvider } from './context/calendar-event.context'
import { useCalendarSettings } from './hooks/use-calendar-settings'
import { BpEvent, EventType } from './models'
import type { Client } from './models/client'

const ClinicianCalendarInner: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const calendarRef = useRef<FullCalendar | null>(null)
  const { openViewEventModal, openAddEventModal } = useCalendarModals()
  const {
    viewCurrentStart,
    viewCurrentEnd,
    setViewCurrentStart,
    setViewCurrentEnd,
    user
  } = useCalendar()
  const { enabledCalendars, setEnabledCalendars, enabledCalendarIds } =
    useCalendarSettings()
  const isTablet = useIsTablet()
  const isMobile = useIsMobile()
  const [isSidebarOpen, setSidebarOpen] = useState(false)

  const getCalendarSettings = useCallback(() => {
    const calendarSettings = localStorage.getItem('clinicianCalendarSettings')
    if (calendarSettings) {
      return JSON.parse(calendarSettings)
    }
    return {}
  }, [])

  const updateCalendarSettings = useCallback(
    (key: string, value: string) => {
      const settings = getCalendarSettings()
      settings[key] = value
      localStorage.setItem(
        'clinicianCalendarSettings',
        JSON.stringify(settings)
      )
    },
    [getCalendarSettings]
  )

  const [selectedView, setSelectedView] = useState<CalendarView>(() => {
    const calendarSettings = getCalendarSettings()
    if (
      calendarSettings.view === CalendarView.TimeGridWeek ||
      calendarSettings.view === CalendarView.DayGridMonth
    ) {
      return calendarSettings.view as CalendarView
    }
    updateCalendarSettings('view', CalendarView.TimeGridWeek)
    return CalendarView.TimeGridWeek
  })

  const [navigationDate, setNavigationDate] = useState<Moment>(() => {
    const date = queryString.parse(location.search).date
    if (date && moment(date, 'YYYY-MM-DD', true).isValid()) {
      return moment(date)
    }
    return moment()
  })

  const { data: scheduleEvents } =
    useClinicianSchedulingControllerGetClinicianSchedule(
      {
        selectedCalendarSystemIds: enabledCalendarIds,
        startDate: viewCurrentStart?.toISOString() || '',
        endDate: viewCurrentEnd?.toISOString() || ''
      },
      {
        query: {
          select: (data: ScheduleResponseDto): EventInput[] => {
            return data.events.map((event: AppointmentDto) => {
              const startMoment = moment(event.start)
              const endMoment = moment(event.end)

              const isAllDay =
                //Check if event is marked as all-day by the source calendar, otherwise check if it matches the all-day time pattern
                Boolean((event as any).extendedProps?.allDay) ||
                (startMoment.format('HH:mm') === '00:00' &&
                  (endMoment.format('HH:mm') === '23:59' ||
                    endMoment.format('HH:mm') === '00:00' ||
                    // For events ending at midnight the next day
                    (endMoment.diff(startMoment, 'days') === 1 &&
                      endMoment.format('HH:mm') === '00:00')))

              return {
                id: event.id,
                title: event.subject,
                start: event.start,
                end: event.end,
                allDay: isAllDay,
                extendedProps: {
                  description: event.description,
                  patient: event.patient,
                  recurrenceTemplate: event.recurrenceTemplate,
                  sourceCalendarType: event.sourceCalendarType,
                  setting: event.setting,
                  serviceType: event.serviceType
                }
              }
            })
          }
        }
      }
    )

  const updateNavigation = useCallback(
    (date: Moment) => {
      if (date.isSame(moment(), 'day')) {
        history.push('/calendar')
      } else {
        history.push(`/calendar?date=${moment(date).format('YYYY-MM-DD')}`)
      }
    },
    [history]
  )

  const handleMiniCalendarDateClick = useCallback(
    (date: Moment) => {
      updateNavigation(date)
      setNavigationDate(date)
    },
    [updateNavigation]
  )

  const handleMainCalendarNavigate = useCallback(
    (date: Moment) => {
      updateNavigation(date)
      setNavigationDate(date)
    },
    [updateNavigation]
  )

  const handleViewChange = useCallback(
    (view: CalendarView) => {
      updateCalendarSettings('view', view)
      setSelectedView(view)
    },
    [updateCalendarSettings]
  )

  const handleMainCalendarDateClick = (date: Moment) => {
    openAddEventModal(date)
  }

  const handleEventClick = (event: AppointmentDto) => {
    const currentEvent: BpEvent = new BpEvent({
      id: event.id,
      subject: event.subject,
      notes: event.description,
      start: event.start,
      end: event.end,
      setting: event.setting,
      eventType: event.patient
        ? EventType.CLIENT_SESSION
        : EventType.OTHER_EVENT,
      patient: event.patient as Client | undefined,
      recurring: event.recurrenceTemplate ? 'recurring' : 'no-repeat',
      recurrenceTemplate: event.recurrenceTemplate,
      clinician_id: user.id,
      serviceType: event.serviceType,
      sourceCalendarType: event.sourceCalendarType
    })

    openViewEventModal(currentEvent)
  }

  const handleAddNewEventClick = () => {
    openAddEventModal()
  }

  const handleDatesSet = (info: DatesSetArg) => {
    setViewCurrentStart(moment(info.start))
    setViewCurrentEnd(moment(info.end))
  }

  return (
    <Flex h="calc(100vh - 80px)" overflow="hidden">
      {' '}
      {/*MODIFY IN ORDER TO ADJUST FOR DYNAMIC VIEW------- CHANGE 80 px to about 92% total viewport*/}
      {/* Sidebar: only show on desktop */}
      {!isTablet && !isMobile && (
        <CalendarSidebar
          navigationDate={navigationDate}
          onMiniCalendarDateClick={handleMiniCalendarDateClick}
          onAddNewEventClick={handleAddNewEventClick}
          enabledCalendars={enabledCalendars}
          setEnabledCalendars={setEnabledCalendars}
        />
      )}
      {/* Calendar */}
      <Box
        flex={1}
        minH="0"
        overflow="hidden"
        padding={{ base: '0', md: '0 var(--chakra-space-8)' }}
      >
        <FullSizeCalendar
          navigationDate={navigationDate}
          selectedView={selectedView}
          onCalendarNavigate={handleMainCalendarNavigate}
          onViewChange={handleViewChange}
          onDateClick={handleMainCalendarDateClick}
          onEventClick={handleEventClick}
          calendarRef={calendarRef}
          scheduleEvents={scheduleEvents || []}
          handleDatesSet={handleDatesSet}
          showSidebarMenuButton={isTablet || isMobile}
          onSidebarMenuClick={() => setSidebarOpen(true)}
        />
      </Box>
      {/* Sidebar Drawer for tablet and mobile */}
      {(isTablet || isMobile) && (
        <Drawer
          isOpen={isSidebarOpen}
          placement="left"
          onClose={() => setSidebarOpen(false)}
        >
          <DrawerOverlay />
          <DrawerContent maxW="260px">
            <DrawerBody p={0}>
              <CalendarSidebar
                navigationDate={navigationDate}
                onMiniCalendarDateClick={handleMiniCalendarDateClick}
                onAddNewEventClick={handleAddNewEventClick}
                enabledCalendars={enabledCalendars}
                setEnabledCalendars={setEnabledCalendars}
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
    </Flex>
  )
}

export const ClinicianCalendar: React.FC = () => {
  return (
    <CalendarProvider>
      <CalendarEventProvider>
        <ClinicianCalendarInner />
      </CalendarEventProvider>
    </CalendarProvider>
  )
}
